import { useEffect, useState } from "react";
import { Copy, Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

const money = (v) => `$${Number(v).toFixed(2)}`;

const Instructions = ({ r, opts, fr, onCancel }) => {
  const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success(fr ? "Copié" : "Copied")).catch(() => {});
  const Row = ({ k, v, c }) => (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-amber-200/60 last:border-0">
      <span className="text-xs text-slate-500">{k}</span>
      <span className="flex items-center gap-1.5 font-semibold text-[#12234A] text-sm text-right">{v}{c && <button type="button" onClick={() => copy(v)} aria-label="Copy" data-testid={`feature-req-copy-${c}`} className="text-slate-400 hover:text-[#F5551E]"><Copy size={13} /></button>}</span>
    </div>
  );
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="feature-request-card">
      <p className="font-bold text-[#12234A] flex items-center gap-2"><Star size={16} className="fill-[#F5551E] text-[#F5551E]" /> {fr ? "En attente du paiement" : "Waiting for your payment"}</p>
      <p className="text-xs text-slate-600 mt-1">{(fr && opts?.instructions_fr) || opts?.instructions_en}</p>
      <div className="mt-3 bg-white rounded-xl px-3 py-1">
        <Row k={fr ? "Forfait" : "Package"} v={r.package_name} />
        <Row k={fr ? "Montant" : "Amount"} v={`${money(r.price)} CAD`} c="amount" />
        <Row k={fr ? "Virement Interac à" : "Interac e-Transfer to"} v={r.emt_email} c="email" />
        <Row k={fr ? "Référence (dans le message)" : "Reference (in the message)"} v={r.ref} c="ref" />
      </div>
      <button type="button" onClick={onCancel} className="mt-3 text-xs font-semibold text-slate-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="feature-request-cancel"><X size={12} /> {fr ? "Annuler la demande" : "Cancel request"}</button>
    </div>
  );
};

export const FeatureAdButton = ({ listing, fr }) => {
  const [opts, setOpts] = useState(null);
  const [req, setReq] = useState(null);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/paid-featured/options").then(({ data }) => setOpts(data)).catch(() => {});
    api.get(`/listings/${listing.id}/feature-request`).then(({ data }) => setReq(data.request)).catch(() => {});
  }, [listing.id]);
  if (!opts?.available || listing.status !== "active") return null;
  if (req?.status === "awaiting_payment") {
    const cancel = async () => { try { await api.post(`/listings/${listing.id}/feature-request/cancel`); setReq({ ...req, status: "cancelled" }); } catch (e) { toast.error(errText(e)); } };
    return <div className="mt-4"><Instructions r={req} opts={opts} fr={fr} onCancel={cancel} /></div>;
  }
  if (listing.featured || listing.featured_scheduled) return null;
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/listings/${listing.id}/feature-request`, { package_id: pick }); setReq(data); setOpen(false); toast.success(fr ? "Demande créée — détails envoyés par courriel" : "Request created — details sent by email"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button onClick={() => { setPick(opts.packages[0]?.id || ""); setOpen(true); }} variant="outline" className="mt-4 rounded-xl gap-2 border-[#F5551E]/40 text-[#F5551E] hover:bg-[#FFF4F0]" data-testid="feature-ad-btn">
        <Star size={15} /> {fr ? "Mettre cette annonce en vedette" : "Feature this ad"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="feature-ad-dialog">
          <DialogHeader>
            <DialogTitle>{fr ? "Mettre en vedette" : "Get a featured spot"}</DialogTitle>
            <DialogDescription>{fr ? "Votre annonce apparaît dans la vedette de la page d'accueil et de sa catégorie. Paiement par virement Interac." : "Your ad shows in the Featured spotlight on the homepage and its category. Pay by Interac e-Transfer."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {opts.packages.map((p) => (
              <button key={p.id} type="button" onClick={() => setPick(p.id)} data-testid={`feature-pkg-${p.id}`}
                className={`w-full flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-colors ${pick === p.id ? "border-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 hover:border-slate-300"}`}>
                <span><span className="block font-semibold text-[#12234A]">{(fr && p.name_fr) || p.name_en}</span><span className="text-xs text-slate-500">{p.days} {fr ? "jours" : "days"}</span></span>
                <span className="font-bold text-[#12234A]">{money(p.price)}</span>
              </button>
            ))}
          </div>
          <Button onClick={submit} disabled={!pick || busy} className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white" data-testid="feature-ad-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : fr ? "Continuer vers le paiement" : "Continue to payment"}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">{fr ? "Votre annonce sera mise en vedette après la confirmation du paiement par notre équipe." : "Your ad is featured once our team confirms the payment."}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
